import React, { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useCustomReducer } from '../../../../../base/customHooks'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'
import IPButton from '../../../../../components/IPButton'
import IPCheckbox from '../../../../../components/IPCheckBox'
import IPInput from '../../../../../components/IPInput'
import Table from '../../../../../components/Table'
import { DeleteVideoCategoryApi } from '../services/deleteVideoCategoryApi'
import { GetVideoCategoryListApi } from '../services/getVideoCategoryListApi'
import { PostVideoCategoryApi } from '../services/postVideoCategoryApi'
import { PutVideoCategoryApi } from '../services/putVideoCategoryApi'
import { IVideoCategory } from '../models/responseModels'

interface IVideoCategoryListState {
  categories: IVideoCategory[]
  editingId: number
  name: string
  orderNo: number
  active: boolean
}

function VideoCategoryList(props: BaseComponentWithContextProps) {
  const getInitialState = (): IVideoCategoryListState => ({
    categories: [],
    editingId: 0,
    name: '',
    orderNo: 0,
    active: true,
  })

  const [state, setState] = useCustomReducer<IVideoCategoryListState>(getInitialState())

  useEffect(() => {
    getCategories()
  }, [])

  const getCategories = async () => {
    props.showLoading()
    const api = new GetVideoCategoryListApi({})
    api.getVideoCategories().then(res => {
      setState({ categories: res })
      props.hideLoading()
    })
  }

  const saveCategory = async () => {
    if (!state.name) return Swal.fire('Hata', 'Kategori adı girmelisiniz.', 'error')

    props.showLoading()
    const payload = { Id: state.editingId, Name: state.name, Active: state.active, OrderNo: state.orderNo }
    const call = state.editingId > 0
      ? new PutVideoCategoryApi({}).putVideoCategory(payload)
      : new PostVideoCategoryApi({}).postVideoCategory(payload)

    call
      .then(() => {
        props.hideLoading()
        setState({ editingId: 0, name: '', orderNo: 0, active: true })
        Swal.fire({
          icon: 'success',
          title: state.editingId > 0 ? 'Kategori güncellendi.' : 'Kategori eklendi.',
          showConfirmButton: true,
          allowOutsideClick: false,
        })
        getCategories()
      })
      .catch((err: any) => {
        props.hideLoading()
        Swal.fire({
          icon: 'error',
          title: err.description,
          showConfirmButton: true,
          allowOutsideClick: false,
        })
      })
  }

  const editCategory = (item: IVideoCategory) => {
    setState({ editingId: item.Id, name: item.Name, orderNo: item.OrderNo ?? 0, active: item.Active })
  }

  const cancelEdit = () => {
    setState({ editingId: 0, name: '', orderNo: 0, active: true })
  }

  const deleteCategory = async (id: number) => {
    Swal.fire({
      icon: 'question',
      title: 'Kategoriyi silmek istediğinize emin misiniz?',
      showConfirmButton: true,
      allowOutsideClick: false,
      showDenyButton: true,
      confirmButtonText: 'Evet',
      denyButtonText: 'Hayır',
    }).then(async result => {
      if (result.isConfirmed) {
        const api = new DeleteVideoCategoryApi({})
        api
          .deleteVideoCategory(id)
          .then(() => {
            Swal.fire({
              icon: 'success',
              title: 'Kategori Silindi',
              showConfirmButton: true,
              allowOutsideClick: false,
            }).then(() => getCategories())
          })
          .catch((err: any) => {
            Swal.fire({
              icon: 'error',
              title: err.description,
              showConfirmButton: true,
              allowOutsideClick: false,
            })
          })
      }
    })
  }

  const tableColumns = useMemo(
    () => [
      { Header: 'Id', accessor: 'id' },
      { Header: 'Ad', accessor: 'name' },
      { Header: 'Sıra', accessor: 'order' },
      { Header: 'Durum', accessor: 'status' },
      { Header: 'İşlemler', accessor: 'actions' },
    ],
    []
  )

  const tableData = useMemo(
    () =>
      state.categories.map(item => ({
        id: item.Id,
        name: item.Name,
        order: item.OrderNo,
        status: item.Active ? 'Aktif' : 'Pasif',
        actions: (
          <div>
            <a role='button' style={{ cursor: 'pointer' }} title='Düzenle' onClick={() => editCategory(item)}>
              <i className='icon-edit' style={{ fontSize: '1.2rem', color: 'green' }}></i>
            </a>
            &nbsp;&nbsp;
            <a role='button' style={{ cursor: 'pointer' }} title='Sil' onClick={() => deleteCategory(item.Id)}>
              <i className='icon-trash-2' style={{ fontSize: '1.2rem', color: 'red' }}></i>
            </a>
          </div>
        ),
      })),
    [state.categories]
  )

  return (
    <div className='container'>
      <div className='content col-lg-12 m-auto'>
        <div className='card'>
          <div className='card-header'>
            <div className='row'>
              <div className='mb-3'>
                <span className='h4'>Video Kategorileri</span>
                <Link to='/admin/HomeAndSiteOperation/video/list'>
                  <button type='button' className='btn btn-secondary' style={{ float: 'right' }}>
                    Videolara Dön
                  </button>
                </Link>
              </div>
            </div>
          </div>
          <div className='card-body'>
            <div className='row mb-5 align-items-end'>
              <div className='col-lg-4'>
                <label className='labeltext'>{state.editingId > 0 ? 'Kategori Adı (Düzenle)' : 'Yeni Kategori Adı'}</label>
                <IPInput
                  type='text'
                  id='name'
                  name='name'
                  className='form-control'
                  placeholder='Örn: Microsoft'
                  value={state.name}
                  onChange={e => setState({ name: e.target.value })}
                />
              </div>
              <div className='col-lg-2'>
                <label className='labeltext'>Sıra</label>
                <IPInput
                  type='number'
                  id='orderNo'
                  name='orderNo'
                  value={state.orderNo}
                  onChange={e => setState({ orderNo: Number(e.target.value) })}
                />
              </div>
              <div className='col-lg-2'>
                <label className='labeltext'>Aktif</label>
                <div>
                  <IPCheckbox
                    id='active'
                    name='active'
                    checked={state.active}
                    onChange={() => setState({ active: !state.active })}
                  />
                </div>
              </div>
              <div className='col-lg-2'>
                <IPButton
                  type='button'
                  className='btn btn-success'
                  text={state.editingId > 0 ? 'Güncelle' : 'Kategori Ekle'}
                  onClick={() => saveCategory()}
                />
                {state.editingId > 0 && (
                  <IPButton type='button' className='btn btn-secondary m-t-5' text='Vazgeç' onClick={() => cancelEdit()} />
                )}
              </div>
            </div>
            <div className='table-responsive'>
              <Table columns={tableColumns} data={tableData} currentPage={0} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
export default componentWithContext(VideoCategoryList)
